/**
 * LIGHT OR DARK, and who decides.
 *
 * THREE STATES, not two. The machine already has an answer -- every OS
 * carries a light/dark preference and the browser reports it -- so the
 * default is to obey it rather than to pick for people. An explicit
 * choice overrides that and is remembered; clearing the choice hands
 * the decision back.
 *
 * Following the system also means following it LIVE: someone whose
 * desktop flips at sunset should see this flip too, without a reload,
 * unless they have said otherwise.
 *
 * The mode is published as `data-theme` on the root element so CSS can
 * read it. Absent means "system" -- index.css keys its dark palette on
 * `prefers-color-scheme` guarded by `:not([data-theme="light"])`, so
 * the attribute is only ever present when somebody chose.
 */
import { useEffect, useState } from 'react';

export type ThemeMode = 'light' | 'dark';

/** Where an explicit choice is remembered, per browser. */
const STORAGE_KEY = 'polywog.theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';

/**
 * The choice this browser has stored, or null when none was made.
 *
 * @returns The stored mode, or null to follow the system. Storage can
 *   throw outright (private windows, blocked site data), and a theme is
 *   not worth a crash -- an unreadable preference is no preference.
 */
export function storedMode(): ThemeMode | null {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return value === 'light' || value === 'dark' ? value : null;
  } catch {
    return null;
  }
}

/**
 * What the machine prefers right now.
 *
 * @returns The OS preference, defaulting to light where the browser
 *   cannot say.
 */
export function systemMode(): ThemeMode {
  return window.matchMedia?.(DARK_QUERY).matches ? 'dark' : 'light';
}

/**
 * Remember a choice, or forget it and follow the system again.
 *
 * @param mode - The chosen mode, or null to defer to the machine.
 */
export function rememberMode(mode: ThemeMode | null): void {
  try {
    if (mode === null) window.localStorage.removeItem(STORAGE_KEY);
    else window.localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    // A browser that will not store a preference still honours one for
    // this session; the attribute below is what actually paints.
  }
}

/**
 * Publish the mode to CSS.
 *
 * @param mode - The active mode.
 * @param chosen - Whether a person chose it. The attribute is written
 *   ONLY for a choice: left absent, `prefers-color-scheme` decides, so
 *   a system-following browser needs no JavaScript to paint correctly
 *   on first frame.
 */
export function publishMode(mode: ThemeMode, chosen: boolean): void {
  const root = document.documentElement;
  if (chosen) root.dataset.theme = mode;
  else delete root.dataset.theme;
}

/**
 * The active mode, and how to change it.
 *
 * @returns The mode in force, and a setter taking null to go back to
 *   following the machine.
 */
export function useThemeMode(): [ThemeMode, (mode: ThemeMode | null) => void] {
  const [chosen, setChosen] = useState<ThemeMode | null>(storedMode);
  const [system, setSystem] = useState<ThemeMode>(systemMode);

  // Follow the machine LIVE while nothing has been chosen.
  useEffect(() => {
    const query = window.matchMedia?.(DARK_QUERY);
    if (!query) return;
    const onChange = (e: MediaQueryListEvent) => setSystem(e.matches ? 'dark' : 'light');
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  const mode = chosen ?? system;
  useEffect(() => publishMode(mode, chosen !== null), [mode, chosen]);

  return [
    mode,
    (next: ThemeMode | null) => {
      rememberMode(next);
      setChosen(next);
    },
  ];
}
